import { useEffect, useRef, useState } from "react";

/**
 * Animated number counter. Uses requestAnimationFrame with an ease-out curve.
 * Runs only on mount (or when `value` changes) so it feels premium, not gimmicky.
 */
export default function CountUp({ value, decimals = 0, prefix = "", suffix = "", duration = 900 }) {
    const [display, setDisplay] = useState(0);
    const startRef = useRef(null);
    const fromRef = useRef(0);

    useEffect(() => {
        if (value == null || isNaN(value)) { setDisplay(value); return; }
        fromRef.current = display;
        startRef.current = null;
        const to = Number(value);
        const from = fromRef.current || 0;
        let raf;
        const step = (t) => {
            if (startRef.current == null) startRef.current = t;
            const elapsed = t - startRef.current;
            const progress = Math.min(1, elapsed / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(from + (to - from) * eased);
            if (progress < 1) raf = requestAnimationFrame(step);
            else setDisplay(to);
        };
        raf = requestAnimationFrame(step);
        return () => raf && cancelAnimationFrame(raf);
        // eslint-disable-next-line
    }, [value]);

    const formatted = typeof display === "number"
        ? display.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : display;
    return <>{prefix}{formatted}{suffix}</>;
}
